import type { Event, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';

/**
 * Parser for JAE Smart DM ERROR files
 * 
 * Converts error messages to aggregated events:
 * - Groups by error type/pattern for clean visualization
 * - Preserves original error messages in yValues
 */
export class SmartDmErrorParser {
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM ERROR file format');
    }
    
    if (!lines[1]?.trim().includes('ERROR Log')) {
      throw new Error('Not a JAE Smart DM ERROR file');
    }
    
    // Extract metadata from header
    const metadata = this.extractMetadata(lines);
    
    // Find the data header and data start
    let dataHeaderIndex = -1;
    let dataStartIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.includes('CLOCK_TICKS,RTC_TIME,STRING_MSG')) {
        dataHeaderIndex = i;
        dataStartIndex = i + 1;
        break;
      }
    }
    
    if (dataHeaderIndex === -1) {
      throw new Error('Could not find data header in JAE Smart DM ERROR file');
    }
    
    // Parse CSV data manually since the format can have inconsistent field counts
    const headerLine = lines[dataHeaderIndex];
    const columnNames = headerLine.split(',').map(name => name.trim());
    
    const dataLines = lines.slice(dataStartIndex).filter(line => line.trim());
    const rawData: Record<string, string>[] = [];
    
    for (const line of dataLines) {
      const values = line.split(',');
      const record: Record<string, string> = {};
      
      columnNames.forEach((colName, index) => {
        if (index < values.length) {
          record[colName] = values[index]?.trim() || '';
        }
      });
      
      rawData.push(record);
    }
    
    const data = rawData;
    
    if (data.length === 0) {
      throw new Error('No valid data found in JAE Smart DM ERROR file');
    }
    
    // Parse error messages into events
    const events = this.parseErrorMessages(data);
    
    return {
      toolType: 'JAE',
      fileType: 'JAE_SMARTDM_ERROR' as WaveFileType,
      events,
      metadata,
      dataPreview: {
        totalCount: data.length,
        first5: data.slice(0, 5)
      }
    };
  }
  
  private static extractMetadata(lines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Extract key-value pairs from header
      if (trimmed.includes(':') && !trimmed.includes('RTC_TIME') && !trimmed.includes('CLOCK_TICKS')) {
        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return metadata;
  }
  
  private static parseErrorMessages(data: Record<string, string>[]): Event[] {
    // Group errors by type/pattern
    const errorGroups = new Map<string, Array<{ timestamp: Date, message: string }>>();
    
    for (const row of data) {
      const timestampStr = row['RTC_TIME'];
      const message = row['STRING_MSG'];
      
      if (!timestampStr || !message) continue;
      
      const timestamp = new Date(timestampStr);
      if (isNaN(timestamp.getTime())) continue;
      
      // Categorize the error
      const errorType = this.categorizeError(message);
      
      if (!errorGroups.has(errorType)) {
        errorGroups.set(errorType, []);
      }
      
      errorGroups.get(errorType)!.push({ timestamp, message });
    }
    
    // Convert to Event objects
    const events: Event[] = [];
    for (const [errorType, errorList] of errorGroups) {
      // Sort by timestamp
      errorList.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      events.push({
        name: errorType,
        xValues: errorList.map(e => e.timestamp),
        yValues: errorList.map(e => e.message)
      });
    }
    
    return events;
  }
  
  private static categorizeError(message: string): string {
    const msg = message.toLowerCase();
    
    if (msg.includes('rdl fail')) {
      return 'RDL Errors';
    }
    
    if (msg.includes('rtc') && msg.includes('init')) {
      return 'RTC Errors';
    }
    
    if (msg.includes('config') && msg.includes('restore')) {
      return 'Config Errors';
    }
    
    if (msg.includes('timeout')) {
      return 'Timeout Errors';
    }
    
    if (msg.includes('signal')) {
      return 'Signal Errors';
    }
    
    if (msg.includes('version')) {
      return 'Version Errors';
    }
    
    // Default catch-all
    return 'General Errors';
  }
} 